import { Link } from "react-router-dom";
import { ArrowRight, Network, Lock, Wifi, Server, Activity, FileSearch } from "lucide-react";

const ABOUT_IMG =
  "https://images.pexels.com/photos/5050305/pexels-photo-5050305.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STRENGTHS = [
  { icon: Network, title: "Enterprise Networks", desc: "Designing and operating routing, switching, segmentation, and WAN across complex topologies." },
  { icon: Lock, title: "Secure Connectivity", desc: "Site-to-site VPN, firewall migration, perimeter hardening, and Zero Trust-aligned segmentation." },
  { icon: Wifi, title: "Wireless Infrastructure", desc: "Robust, secure wireless across single-site offices and distributed campuses." },
  { icon: Server, title: "Virtualization & Linux", desc: "VMware platforms, Linux server operations, and the backbone services that depend on both." },
  { icon: Activity, title: "Mission-Critical Operations", desc: "Run-book, change-control, and monitoring practices for environments that can't tolerate drift." },
  { icon: FileSearch, title: "Real-World Troubleshooting", desc: "Packet captures, deep protocol analysis, and methodical root-cause work." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="relative border-b border-slate-800/80 bg-[#060B15] overflow-hidden">
        <div className="absolute inset-0 bek-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 bek-radial-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="bek-eyebrow mb-5">About bekTech</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold tracking-tight max-w-4xl">
            A consulting company built on{" "}
            <span className="bek-chrome">deep, hands-on</span> network and security work.
          </h1>
          <p className="mt-6 text-slate-300 max-w-3xl leading-relaxed text-lg">
            bekTech is a network and cybersecurity consulting firm. We help our clients
            design, secure, and operate the infrastructure their business actually runs on —
            on-site, remote, or hybrid.
          </p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="bek-eyebrow mb-4">Who We Are</div>
            <h2 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight">
              We focus on infrastructure that has to work — and stay secure.
            </h2>
            <div className="mt-8 space-y-5 text-slate-300 leading-relaxed">
              <p>
                Our team brings years of hands-on experience across enterprise networks, secure connectivity,
                firewall migration, wireless infrastructure, virtualization, and Linux systems. We've supported
                mission-critical operations and worked through the kind of real-world troubleshooting that
                only shows up at 2 a.m.
              </p>
              <p>
                We work as an embedded extension of your IT team, not a vendor on the other side of a quote.
                Whether we're segmenting a flat network, migrating firewalls between platforms, modernizing
                a wireless deployment, or preparing your environment for audit — our value is in the engineering depth,
                operational discipline, and documentation we leave behind.
              </p>
              <p>
                We believe good consulting is measured by what continues to work after we've moved on.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-slate-800 overflow-hidden">
              <img src={ABOUT_IMG} alt="Modern data center" className="w-full h-80 object-cover opacity-90" loading="lazy" />
            </div>
            <div className="mt-6 p-6 border border-slate-800 bg-[#0B1220]">
              <div className="bek-eyebrow mb-3">Operating Principles</div>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" />
                  Engineering over slideware.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" />
                  Documented changes. Reproducible outcomes.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" />
                  Security by design, not by retrofit.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" />
                  Leave your team stronger than we found it.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#060B15] border-y border-slate-800/80">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="bek-eyebrow mb-4">Where We Go Deep</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold tracking-tight">
              Hands-on across the stack our clients <span className="bek-chrome">depend on.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STRENGTHS.map((s) => (
              <div
                key={s.title}
                data-testid={`strength-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#080F1C] border border-slate-800 p-8 bek-card-hover"
              >
                <s.icon className="w-6 h-6 text-slate-300 mb-5" />
                <h3 className="text-lg text-white font-semibold mb-3">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mt-16 pt-12 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="text-2xl text-white font-semibold">Let's build something reliable.</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">
                We'd love to hear about what you're working on.
              </p>
            </div>
            <Link to="/contact" data-testid="about-cta" className="bek-cta">
              Request Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
